import React from 'react'
import { useTranslation } from 'react-i18next'
import { DollarSign, Calendar, User, Car, Eye } from 'lucide-react'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, ShadcnButton } from '../ui'
import { Sale } from '../../types'
import { formatCurrency, formatDateToBR } from '../../utils/formatters'
import { cn } from '../../utils/cn'

interface SalesListViewProps {
 sales: Sale[]
 isLoading: boolean
 onViewDetails: (saleId: string) => void
 getStatusIcon: (status: Sale['status']) => React.ReactNode
 getStatusLabel: (status: Sale['status']) => string
 page: number
 totalPages: number
 onPageChange: (page: number) => void
}

export const SalesListView: React.FC<SalesListViewProps> = ({
 sales,
 isLoading,
 onViewDetails,
 getStatusIcon,
 getStatusLabel,
 page,
 totalPages,
 onPageChange,
}) => {
 const { t } = useTranslation()
 if (isLoading && sales.length === 0) {
 return (
 <div className="flex items-center justify-center py-12">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
 </div>
 )
 }

 if (sales.length === 0) {
 return (
 <div className="text-center py-12">
 <p className="text-muted-foreground">{t('financialSales.empty')}</p>
 </div>
 )
 }

 return (
 <div className="space-y-4">
 <div className="overflow-x-auto">
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Data</TableHead>
 <TableHead>Cliente</TableHead>
 <TableHead>Veículo</TableHead>
 <TableHead>Vendedor</TableHead>
 <TableHead>Valor Total</TableHead>
 <TableHead>Valor Final</TableHead>
 <TableHead>Status</TableHead>
 <TableHead>Ações</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {sales.map((sale) => {
 const totalValue = parseFloat(sale.totalValue.toString())
 const finalValue = parseFloat(sale.finalValue.toString())
 const vehicleName = sale.vehicle
 ? `${sale.vehicle.brand?.name || ''} ${sale.vehicle.model?.name || ''} ${sale.vehicle.modelYear || ''}`
 : 'N/A'
 const customerName = sale.customer?.name || 'N/A'
 const salespersonName = sale.salesperson?.name || 'N/A'

 return (
 <TableRow
 key={sale.id}
 className={cn('cursor-pointer hover:bg-muted/60')}
 onClick={() => onViewDetails(sale.id)}
 role="button"
 tabIndex={0}
 onKeyDown={(e) => {
 if (e.key === 'Enter' || e.key === ' ') {
 e.preventDefault()
 onViewDetails(sale.id)
 }
 }}
 >
 <TableCell>
 <div className="flex items-center gap-2">
 <Calendar className="h-4 w-4 text-muted-foreground"/>
 <span>{formatDateToBR(sale.saleDate)}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 <User className="h-4 w-4 text-muted-foreground"/>
 <span>{customerName}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 <Car className="h-4 w-4 text-muted-foreground"/>
 <span className="truncate max-w-xs">{vehicleName}</span>
 </div>
 </TableCell>
 <TableCell>
 <span>{salespersonName}</span>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 <DollarSign className="h-4 w-4 text-green-600"/>
 <span className="font-medium">{formatCurrency(totalValue)}</span>
 </div>
 </TableCell>
 <TableCell>
 <span className="font-semibold">{formatCurrency(finalValue)}</span>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 {getStatusIcon(sale.status)}
 <span>{getStatusLabel(sale.status)}</span>
 </div>
 </TableCell>
 <TableCell>
 <ShadcnButton
 variant="ghost"
 size="sm"
 type="button"
 onClick={(e) => {
 e.stopPropagation()
 onViewDetails(sale.id)
 }}
 >
 <Eye className="h-4 w-4 mr-1"/>
 Ver
 </ShadcnButton>
 </TableCell>
 </TableRow>
 )
 })}
 </TableBody>
 </Table>
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between">
 <p className="text-sm text-muted-foreground">
 Página {page} de {totalPages}
 </p>
 <div className="flex gap-2">
 <ShadcnButton
 variant="outline"
 size="sm"
 onClick={() => onPageChange(page - 1)}
 disabled={page === 1}
 >
 Anterior
 </ShadcnButton>
 <ShadcnButton
 variant="outline"
 size="sm"
 onClick={() => onPageChange(page + 1)}
 disabled={page === totalPages}
 >
 Próxima
 </ShadcnButton>
 </div>
 </div>
 )}
 </div>
 )
}

